import React, { useState, useRef } from 'react';
import { 
  Upload,
  Search, 
  X, 
  Image as ImageIcon, 
  Loader2, 
  Eye,
  ShoppingCart
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import SiparisOlusturModal from './SiparisOlusturModal';
import config from '../../config/config';

/**
 * Gelişmiş Görsel Arama Komponenti
 * Admin + müşteri portalı aynı CLIP motorunu kullanır (farklı API yolu).
 */
const AdvancedImageSearch = ({
  onClose,
  onSelectDesen,
  apiClient = api,
  uploadUrl = '/api/baski/advanced-search/upload',
  variant = 'admin',
  title = 'Görsel Arama',
  subtitle = 'Önce giysideki kumaş desenini (leopar, çiçek, çizgi…) okur, sonra benzer baskıları arar',
  selectLabel = 'Seç',
  showAdminSiparis = true,
  textFilterPlaceholder = 'Örn: Jante, janjan krep, rpt, yeni, onaylı…'
}) => {
  const isCustomer = variant === 'customer';
  const [uploadedImage, setUploadedImage] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDesen, setSelectedDesen] = useState(null);
  const [textFilter, setTextFilter] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showSiparisModal, setShowSiparisModal] = useState(false);
  const [selectedDesenForSiparis, setSelectedDesenForSiparis] = useState(null);
  const fileInputRef = useRef(null);

  // Güvenli array normalizasyonu
  const normalizeGorselUrls = (gorselUrls) => {
    if (!gorselUrls) return [];
    if (Array.isArray(gorselUrls)) {
      return gorselUrls.filter(Boolean).map(url => {
        if (typeof url === 'string') return url;
        return String(url);
      });
    }
    if (typeof gorselUrls === 'string') {
      try {
        const parsed = JSON.parse(gorselUrls);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (e) {
        const matches = gorselUrls.match(/\/uploads\/[^"'\s,\]]+/g);
        return matches || [];
      }
    }
    return [];
  };

  // Güvenli görsel URL üretici (müşteri portalında /uploads nginx’te olmayabilir → thumbnail API)
  const toAbsoluteUploadUrl = (relOrAbs) => {
    if (!relOrAbs) return '';
    const raw = String(relOrAbs).trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    const path = raw.startsWith('/') ? raw : `/${raw}`;

    // Desen görselleri: img auth göndermediği için public thumbnail API
    if (path.includes('/baski/desenler/')) {
      const filename = path.split('/baski/desenler/')[1]?.split('?')[0];
      if (filename) {
        const origin = (typeof window !== 'undefined' && window.location?.origin)
          || config.backendUrl
          || '';
        return `${origin.replace(/\/$/, '')}/api/baski/thumbnail/${encodeURIComponent(filename)}?width=320&height=320&webp=true`;
      }
    }

    const base = (config.uploadsUrl || config.backendUrl || '').replace(/\/$/, '');
    return `${base}${path}`;
  };

  const getSafeImageUrl = (obj) => {
    if (!obj) return '';
    const arr = normalizeGorselUrls(obj.gorsel_urls || obj.gorselUrls);
    const direct = obj.searchImageUrl || obj.gorsel_url || arr[0];
    return toAbsoluteUploadUrl(direct);
  };

  const resolveImageUrl = (url) => toAbsoluteUploadUrl(url);

  // Resim yükleme (dosya seçimi)
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    processImageFile(file);
  };

  // Resim dosyasını işleme
  const processImageFile = (file) => {
    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    // Dosya boyutu kontrolü (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage({
        file,
        preview: e.target.result,
        name: file.name,
        size: file.size
      });
    };
    reader.readAsDataURL(file);
  };

  // Drag & Drop işlemleri
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processImageFile(files[0]);
      toast.success('Resim sürüklendi!');
    }
  };

  // Clipboard paste işlemi
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          processImageFile(file);
          toast.success('Resim yapıştırıldı!');
          break;
        }
      }
    }
  };

  // Arama başlatma
  const handleSearch = async () => {
    if (!uploadedImage) {
      toast.error('Lütfen önce bir resim yükleyin');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', uploadedImage.file);
      if (textFilter.trim()) {
        formData.append('textFilter', textFilter.trim());
      }

      const response = await apiClient.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        // CLIP ile tüm desenler taranır; ilk indeksleme uzun sürebilir
        timeout: 300000
      });

      if (response.data.success) {
        const data = response.data.data || {};
        setSearchResults(data.results || []);
        const found = data.totalFound || data.results?.length || 0;
        if (found === 0 && data.message) {
          toast.error(data.message);
        } else if (data.exactMatchFound === false && data.message) {
          toast(data.message, { icon: 'ℹ️', duration: 5000 });
        } else if (textFilter.trim()) {
          toast.success(`${found} benzer desen (filtre: “${textFilter.trim()}”)`);
        } else {
          toast.success(`${found} benzer desen bulundu`);
        }
      } else {
        toast.error(response.data.message || 'Arama başarısız');
      }
    } catch (error) {
      toast.error('Arama sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Mevcut desenlerde arama fonksiyonu kaldırıldı (şu an UI'da kullanılmıyor)

  // Sonuçları filtrele (backend'de 40% minimum zaten uygulanıyor)
  const filteredResults = searchResults
    .slice(0, 100) // Maksimum 100 sonuç
    .sort((a, b) => {
      const scoreA = a.overallScore || a.similarity?.overall || 0;
      const scoreB = b.overallScore || b.similarity?.overall || 0;
      return scoreB - scoreA; // Benzerlik skoruna göre sırala
    });

  // Desen seçme
  const handleSelectDesen = (desen) => {
    setSelectedDesen(desen);
    if (onSelectDesen) {
      onSelectDesen(desen);
    }
  };


  // Resim büyütme
  const handleImageClick = (result) => {
    const url = getSafeImageUrl(result);
    if (!url) {
      toast.error('Görsel URL bulunamadı');
      return;
    }
    // gorsel_urls'i normalize et
    const normalizedUrls = normalizeGorselUrls(result.gorsel_urls);
    setSelectedImage({ ...result, searchImageUrl: url, gorsel_urls: normalizedUrls });
    setShowImageModal(true);
  };

  // Sipariş oluştur
  const handleCreateSiparis = (desen) => {
    setSelectedDesenForSiparis(desen);
    setShowSiparisModal(true);
  };

  // Sipariş modal kapat
  const handleSiparisModalClose = () => {
    setShowSiparisModal(false);
    setSelectedDesenForSiparis(null);
  };

  // Sipariş başarılı callback'i şu an kullanılmıyor; ileride entegrasyon için eklenebilir

  // Arama temizleme
  const handleClearSearch = () => {
    setUploadedImage(null);
    setSearchResults([]);
    setSelectedDesen(null);
    setTextFilter('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                {title}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {subtitle}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          <div 
            className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-80px)] sm:max-h-[calc(90vh-80px)]"
            onPaste={handlePaste}
            tabIndex={0}
          >
            <div className="space-y-4">

              {/* Resim Yükleme ve Filtreler */}
              <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4">
                {/* Resim Yükleme Alanı */}
                <div 
                  className={`border-2 border-dashed rounded-lg p-3 text-center transition-all duration-200 flex-1 ${
                    isDragOver 
                      ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 scale-[1.01]' 
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {uploadedImage ? (
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden shadow-sm flex-shrink-0">
                        <img
                          src={uploadedImage.preview}
                          alt="Yüklenen resim"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{uploadedImage.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{(uploadedImage.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button
                        onClick={handleClearSearch}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-sm"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {isDragOver ? (
                        <>
                          <div className="mx-auto h-8 w-8 text-blue-500 animate-bounce">
                            <Upload className="h-full w-full" />
                          </div>
                          <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                            Resmi bırakın!
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600">
                            <Upload className="h-full w-full" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Resim sürükleyin, yapıştırın veya{' '}
                              <button
                                onClick={() => fileInputRef.current?.click()}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium underline"
                              >
                                dosya seçin
                              </button>
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            PNG, JPG, GIF, WebP • Max 10MB • Ctrl+V
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Arama Butonu */}
                <div className="flex flex-col space-y-3 w-full lg:w-64">
                  {uploadedImage && (
                    <button
                      onClick={handleSearch}
                      disabled={loading}
                      className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      {loading ? 'Aranıyor...' : 'Ara'}
                    </button>
                  )}
                  {searchResults.length > 0 && (
                    <button
                      onClick={handleClearSearch}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-center"
                    >
                      Temizle
                    </button>
                  )}
                </div>
              </div>

              {/* Metin ile daralt: cari / kumaş / not / durum */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Metin filtresi (isteğe bağlı)
                </label>
                <input
                  type="text"
                  value={textFilter}
                  onChange={(e) => setTextFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && uploadedImage && !loading) {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  placeholder={textFilterPlaceholder}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {isCustomer
                    ? 'Desen no, model no veya kumaş adı ile daraltır; benzerlik kendi desenlerinizde aranır.'
                    : 'Müşteri, kumaş/stok, desen no, not veya onay durumu ile daraltır; görsel benzerliği bu kümede arar.'}
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />


              {selectedDesen && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Seçilen Desen: {selectedDesen.desen_no}
                  </h4>
                  
                  {/* Seçilen Desen Görseli */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-24 h-24 bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={getSafeImageUrl(selectedDesen)}
                          alt={selectedDesen.desen_no}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden w-full h-full items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 dark:text-white">{selectedDesen.desen_no}</h5>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Benzerlik: %{selectedDesen.overallScore || selectedDesen.similarity?.overall || 0}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {new Date(selectedDesen.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pattern Etiketleri */}
                  {(selectedDesen.patternTags && selectedDesen.patternTags.length > 0) && (
                    <div className="flex flex-wrap gap-2">
                      {selectedDesen.patternTags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200"
                        >
                          #{tag.label} ({Math.round(((tag.score ?? tag.confidence ?? 0) * 100))}%)
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Varyantlar */}
                  {selectedDesen.groupVariants && selectedDesen.groupVariants.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white">Varyantlar</h5>
                      <div className="flex flex-wrap gap-2">
                        {selectedDesen.groupVariants.slice(0, 8).map((variant, idx) => (
                          <div
                            key={idx}
                            className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              const url = resolveImageUrl(variant.imageUrl);
                              setSelectedDesen(prev => prev ? {
                                ...prev,
                                searchImageUrl: url,
                                gorsel_urls: [url, ...(prev.gorsel_urls || []).filter(item => item !== url)]
                              } : prev);
                            }}
                          >
                            <img
                              src={resolveImageUrl(variant.imageUrl)}
                              alt={`${selectedDesen.desen_no}-varyant-${idx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden w-full h-full items-center justify-center">
                              <ImageIcon className="h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedDesen.primaryGarmentType && (
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Giysi Türü: <span className="font-medium text-gray-900 dark:text-white">{selectedDesen.primaryGarmentType.label || selectedDesen.primaryGarmentType.name || selectedDesen.primaryGarmentType.id}</span>
                    </div>
                  )}

                  {/* Sipariş Oluştur Butonu */}
                  {/* This block is removed as per the edit hint */}
                </div>
              )}

              {/* Arama Sonuçları */}
              {searchResults.length > 0 && !selectedDesen && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Sonuçlar ({filteredResults.length})
                  </h4>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                    {filteredResults.map((result) => (
                      <div
                        key={`${result.id}-${result.searchImageUrl}`}
                        className={`bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden transition-all ${
                          selectedDesen?.id === result.id ? 'ring-2 ring-blue-500 border-blue-500' : 'hover:shadow-md'
                        }`}
                      >
                        {/* Görsel */}
                        <div 
                          className="aspect-square bg-gray-100 dark:bg-gray-600 relative cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => handleImageClick(result)}
                        >
                          <img
                            src={getSafeImageUrl(result)}
                            alt={result.desen_no}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Görsel yüklenemedi:', result.searchImageUrl || result.gorsel_url);
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden w-full h-full items-center justify-center">
                            <ImageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 dark:text-gray-500" />
                          </div>
                          
                          {/* Benzerlik Skoru */}
                          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-white dark:bg-gray-800 rounded-full px-1.5 py-0.5 sm:px-2 sm:py-1 shadow-sm">
                            <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                              %{result.overallScore || result.similarity?.overall || 0}
                            </span>
                          </div>
                          
                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                            <div className="opacity-0 hover:opacity-100 transition-opacity">
                              <Eye className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                            </div>
                          </div>
                        </div>

                        {/* İçerik */}
                        <div className="p-2 sm:p-3">
                          <h5 className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white truncate">
                            {result.desen_no}
                          </h5>
                          {(result.musteri_adi || result.stok_kart_adi) && (
                            <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-0.5 truncate">
                              {[result.musteri_adi, result.stok_kart_adi].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {new Date(result.created_at).toLocaleDateString('tr-TR')}
                          </p>
                          {result.primaryGarmentType && (
                            <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">
                              Giysi: {result.primaryGarmentType.label || result.primaryGarmentType.name || result.primaryGarmentType.id}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(result.garmentTags || []).slice(0, 1).map((tag, idx) => (
                              <span
                                key={`garment-${idx}`}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                              >
                                {tag.label}
                              </span>
                            ))}
                            {(result.patternTags || []).slice(0, 2).map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200"
                              >
                                #{tag.label}
                              </span>
                            ))}
                          </div>
                          {!!result.reasonText && (
                            <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 line-clamp-2">
                              {result.reasonText}
                            </p>
                          )}
                          
                          {showAdminSiparis && !isCustomer ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCreateSiparis(result);
                              }}
                              className="w-full mt-2 inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-white bg-purple-600 border border-transparent rounded hover:bg-purple-700 focus:outline-none focus:ring-1 focus:ring-purple-500 transition-colors"
                            >
                              <ShoppingCart className="h-3 w-3 mr-1" />
                              Sipariş Oluştur
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectDesen(result);
                                if (typeof onClose === 'function') onClose();
                              }}
                              className="w-full mt-2 inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                            >
                              {selectLabel}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Boş Durum */}
              {searchResults.length === 0 && !loading && (
                <div className="text-center py-12">
                  <Search className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
                  <p className="mt-2 text-gray-500 dark:text-gray-400">Henüz arama yapılmadı</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          {selectedDesen && (
            <div className="border-t border-gray-100 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Seçili: {selectedDesen.desen_no}
                  </span>
                  {/* This block is removed as per the edit hint */}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setSelectedDesen(null);
                      // setSelectedDesenVaryantlari([]); // This line is removed
                      // setVaryantMiktarlari({}); // This line is removed
                    }}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Temizle
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resim Büyütme Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={() => setShowImageModal(false)} />
            
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={getSafeImageUrl(selectedImage)}
                      alt={selectedImage.desen_no}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Modal görsel yüklenemedi:', selectedImage.searchImageUrl || selectedImage.gorsel_url);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-full items-center justify-center">
                      <ImageIcon className="h-6 w-6 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedImage.desen_no}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Benzerlik: %{selectedImage.overallScore || selectedImage.similarity?.overall || 0}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleSelectDesen(selectedImage)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
                  >
                    <Eye className="h-4 w-4 mr-2 inline" />
                    Seç
                  </button>
                  <button
                    onClick={() => setShowImageModal(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Resim ve Detaylar */}
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Resim */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <img
                        src={getSafeImageUrl(selectedImage)}
                        alt={selectedImage.desen_no}
                        className="w-full max-h-[50vh] object-contain rounded-lg shadow-lg"
                        onError={(e) => {
                          console.error('Büyük görsel yüklenemedi:', selectedImage.searchImageUrl || selectedImage.gorsel_url);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-32 items-center justify-center">
                        <ImageIcon className="h-12 w-12 text-gray-400" />
                      </div>
                    </div>
                    
                    {/* Ek Görseller */}
                    {(() => {
                      const normalizedUrls = normalizeGorselUrls(selectedImage.gorsel_urls);
                      return normalizedUrls.length > 1 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">Diğer Görseller</h4>
                        <div className="grid grid-cols-4 gap-2">
                            {normalizedUrls.slice(1, 5).map((url, index) => (
                            <div
                              key={index}
                              className="aspect-square bg-gray-100 dark:bg-gray-600 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => {
                                setSelectedImage({...selectedImage, searchImageUrl: url});
                              }}
                            >
                              <img
                                src={url}
                                alt={`${selectedImage.desen_no} - ${index + 2}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="hidden w-full h-full items-center justify-center">
                                <ImageIcon className="h-4 w-4 text-gray-400" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      );
                    })()}
                  </div>

                  {/* Detaylar */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">Desen Detayları</h4>
                    
                    {/* Benzerlik Skorları */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Genel Benzerlik</p>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          %{selectedImage.overallScore || selectedImage.similarity?.overall || 0}
                        </p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium">Renk Benzerliği</p>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          %{selectedImage.colorSimilarity || selectedImage.similarity?.colors || 0}
                        </p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                        <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Özellik Benzerliği</p>
                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          %{selectedImage.featureSimilarity || selectedImage.similarity?.features || 0}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Oluşturulma Tarihi</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {new Date(selectedImage.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>

                    {/* Desen Bilgileri */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Desen Bilgileri</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Desen No:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{selectedImage.desen_no}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Toplam Görsel:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{selectedImage.gorsel_urls?.length || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Durum:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">Aktif</span>
                        </div>
                        {!!selectedImage.reasonText && (
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Eslesme Nedeni:</span>
                            <p className="mt-1 text-xs text-gray-900 dark:text-white">{selectedImage.reasonText}</p>
                          </div>
                        )}
                        {Array.isArray(selectedImage.matchedBy) && selectedImage.matchedBy.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {selectedImage.matchedBy.map((item) => (
                              <span
                                key={item}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Aksiyon Butonları */}
                    <div className="flex space-x-3">
                      {showAdminSiparis && !isCustomer ? (
                        <button
                          onClick={() => handleCreateSiparis(selectedImage)}
                          className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg"
                        >
                          <ShoppingCart className="h-4 w-4 mr-2 inline" />
                          Sipariş Oluştur
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            handleSelectDesen(selectedImage);
                            setShowImageModal(false);
                            if (typeof onClose === 'function') onClose();
                          }}
                          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
                        >
                          {selectLabel}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sipariş Oluştur Modal (yalnızca admin) */}
            {showAdminSiparis && !isCustomer && showSiparisModal && (
              <SiparisOlusturModal
                isOpen={showSiparisModal}
                onClose={handleSiparisModalClose}
                editData={null}
                selectedDesen={selectedDesenForSiparis}
                hideDesenSelection={true}
              />
            )}
    </div>
  );
};

export default AdvancedImageSearch;
